import StatusDot from '@/components/ui/StatusDot.tsx';
import { InfoCard, InfoCardHeader } from '@/components/ui/InfoCard.tsx';
import { TourOptionOccupancyDto } from '@/features/dashboard/tours/types/dashboard/dto/TourOptionOccupancyDto.ts';
import {
  freePlacesLabel,
  occupancyLabel,
} from '@/features/dashboard/tours/types/availability.utils.ts';
import { getOptionOccupancyStatusDisplay } from '@/features/dashboard/tours/types/dashboard/optionOccupancyStatus.utils.ts';
import OccupancyBar from '@/features/dashboard/tours/components/options/OccupancyBar.tsx';
import { optionMetaLabel } from '@/features/dashboard/tours/components/options/optionLabels.ts';

interface OptionOccupancyCardProps {
  options: TourOptionOccupancyDto[];
}

/**
 * Ile miejsc zostało na każdej opcji i w każdym jej wariancie. Wariant bez
 * własnego limitu dzieli sufit z opcją, więc pokazuje samą liczbę zapisanych —
 * pasek i „wolne miejsca” odnosiłyby się do limitu, którego wariant nie ma.
 */
const OptionOccupancyCard = ({ options }: OptionOccupancyCardProps) => (
  <InfoCard>
    <InfoCardHeader
      title="Obłożenie opcji"
      aside={
        <span className="text-[13px] text-neutral-500">
          limit na opcję i na wariant
        </span>
      }
    />

    <div className="divide-y divide-neutral-100">
      {options.map((option) => {
        const status = getOptionOccupancyStatusDisplay(option.availability);

        return (
          <div key={option.optionId} className="px-5 py-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="min-w-[220px] flex-1">
                <p className="text-[15px] font-bold text-ink">{option.name}</p>
                <p className="text-[13px] text-neutral-500">
                  {optionMetaLabel(option)}
                </p>
              </div>

              <div className="w-[200px] shrink-0">
                <div className="flex items-baseline justify-between gap-2 text-[13px]">
                  <span className="font-semibold tabular-nums text-ink">
                    {occupancyLabel(option.availability)}
                  </span>
                  <span className="text-neutral-500">
                    {freePlacesLabel(option.availability)}
                  </span>
                </div>
                <OccupancyBar
                  availability={option.availability}
                  className="mt-1.5"
                />
              </div>

              <StatusDot
                tone={status.tone}
                label={status.label}
                className="w-[130px] shrink-0 text-[13px]"
              />
            </div>

            <div className="mt-3 flex flex-col gap-2 border-l-2 border-neutral-100 pl-3.5">
              {option.variants.map((variant) => {
                const hasOwnLimit = variant.availability.limit !== null;

                return (
                  <div
                    key={variant.variantId}
                    className="flex items-center gap-3"
                  >
                    <span className="min-w-0 flex-1 truncate text-[13px] text-neutral-700">
                      {variant.name}
                    </span>
                    <span className="w-[60px] shrink-0 text-right text-[13px] font-semibold tabular-nums text-neutral-700">
                      {occupancyLabel(variant.availability)}
                    </span>
                    {hasOwnLimit && (
                      <>
                        <OccupancyBar
                          availability={variant.availability}
                          className="h-1 w-[96px] shrink-0"
                        />
                        <span className="w-[130px] shrink-0 text-[13px] text-neutral-500">
                          {freePlacesLabel(variant.availability)}
                        </span>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  </InfoCard>
);

export default OptionOccupancyCard;
